import Papa from 'papaparse';
import Promise from 'bluebird';
import _ from 'underscore';
import { withUiBlock } from '@bingads-webui/uiblocker';

import { stringKeys } from './constants';
import { OptimizedSHA256 } from '../crypto/sha256';
import { chunkArray } from './util';

export const MaxCsvRows = 4000001;
export const NoErrorLineMessage = 'Success';

export const InvalidFileError = new Error(stringKeys.invalidFileErrorMessage);
export const MaxRowsExceedError = new Error(stringKeys.maxRowsExceedErrorMessage);
export const NoHeaderError = new Error(stringKeys.noHeaderErrorMessage);

// Row error
export const CRMIdNotAllowedError =
  new Error('This file contains CRM ID(s) that aren\'t supported for your account. Please remove the column, including the header.');
export const MSIDNotAllowedError =
  new Error('This file contains MSID that aren\'t supported for your account. Please remove the column, including the header.');
export const UnsupportedHeaderError =
  new Error('This file contains a header that is not supported.'); // PM
export const ColumnHeaderMissingError = new Error('Headers of some columns are missing.'); // PM
export const InvalidEmailError = new Error('Enter a valid email address.');
export const InvalidCRMIdError = new Error('Enter a valid CRM ID.');
export const InvalidMSIDError = new Error('Enter a valid MSID.');

// Private data
const EmailAddressFormatRegex = /^(?=^.*@.*\.[a-z0-9-]*[a-z-][a-z0-9-]*$)(?=^.{1,64}@.*$)(?=^.*@.{4,255}$)[a-z0-9\x21\x23\x24\x25\x26\x27\x2A\x2B\x2D\x2F\x3D\x3F\x5E\x5F\x60\x7B\x7C\x7D\x7E](\.?[a-z0-9\x21\x23\x24\x25\x26\x27\x2A\x2B\x2D\x2F\x3D\x3F\x5E\x5F\x60\x7B\x7C\x7D\x7E]){0,}@[a-z0-9][a-z0-9-]{0,62}\.([a-z0-9-]{1,63}\.)*[a-z0-9-]{1,5}[a-z0-9]$/i;

const ErrorColumnTitle = 'Result';

const isValidSHA256Format = x => x.replace(/\s+/g, '').match(/^[a-f0-9]{64}$/i);
const normalizeData = x =>
  x.trim().toLowerCase();

const defaultCustomerListFileConfig = {
  isCRMIdAllowed: false,
  isMSIDAllowed: false,
  isFileHashed: false,
  delimiter: ',',
  header: false,
  comments: true,
  skipEmptyLines: 'greedy',
};

const SHA256 = new OptimizedSHA256();

const parserConfigKeys = ['delimiter', 'header', 'comments', 'skipEmptyLines'];

const headerOptions = {
  Email: {
    text: 'email',
    isPII: true,
    validator: (value, config) => {
      const valid = (value === '') || ((config.isFileHashed === true) ?
        isValidSHA256Format(value) : value.match(EmailAddressFormatRegex));

      return valid ? null : InvalidEmailError;
    },
  },
  CRM_Id: {
    text: 'crm id',
    isPII: false,
    validator: (value, config) => {
      if (value === '') {
        return null;
      }

      if (!config.isCRMIdAllowed) {
        return CRMIdNotAllowedError;
      }

      return value.length < 100 ? null : InvalidCRMIdError;
    },
  },
  MSID: {
    text: 'msid',
    isPII: false,
    validator: (value, config) => {
      if (value === '') {
        return null;
      }

      if (!config.isMSIDAllowed) {
        return MSIDNotAllowedError;
      }

      return /^[0-9A-Fa-f]{32}$/.test(value) ? null : InvalidMSIDError
    },
  },
};

const missingHeaderOption = {
  text: '',
  isPII: false,
  validator: value => (value === '' ? null : ColumnHeaderMissingError),
};

const unsupportedHeaderOption = {
  text: '',
  isPII: false,
  validator: value => (value === '' ? null : UnsupportedHeaderError),
};

export class CustomerListFile {
  constructor(domFileObject, config) {
    // Basic information
    this.file = domFileObject;
    this.config = _.defaults(config, defaultCustomerListFileConfig);

    this.parserResult = null;

    // Csv related data
    this.allCsvRows = [];

    this.maxColumns = 0;
    this.headerRow = null;
    this.headerMapping = [];

    this.dataRows = [];
    this.rowsErrors = [];

    this.validationResultMeta = null;
    this.uploadFileContent = null;
  }

  parseFileToObjectAsync() {
    const parserConfig = _.pick(this.config, parserConfigKeys);

    return new Promise((resolve, reject) => {
      parserConfig.complete = resolve;
      parserConfig.error = reject;

      Papa.parse(this.file, parserConfig);
    }).then((parserResult) => {
      this.parserResult = parserResult;
      this.parserErrors = parserResult.errors;

      if (this.parserErrors.length > 0) {
        throw InvalidFileError;
      }
    });
  }

  parseMetadata() {
    // Basic data size validation

    // Filter comment lines
    const allCsvRows = _.filter(this.parserResult.data, row => row.length > 0 && row[0].replace(/^\s+|\s+$/g, '').indexOf('#') !== 0);

    if (allCsvRows.length === 0) {
      throw NoHeaderError;
    }

    if (allCsvRows.length > MaxCsvRows) {
      throw MaxRowsExceedError;
    }
    this.allCsvRows = allCsvRows;

    // Seperate header and data rows
    [this.headerRow, ...this.dataRows] = allCsvRows;

    this.parseHeaders(this.headerRow, this.dataRows);
  }

  parseHeaders = (headerRow, dataRows) => {
    const maxColumns = _.max([headerRow.length, ..._.map(dataRows || [], x => x.length)]);
    this.maxColumns = maxColumns;

    const parsedHeaders = [];
    for (let index = 0; index < maxColumns; index += 1) {
      if (index < headerRow.length) {
        const field = headerRow[index];

        if (field === null || field === '') {
          parsedHeaders.push(missingHeaderOption);
        }

        const normalizedHeader = normalizeData(field);
        const headerOption = _.findWhere(headerOptions, { text: normalizedHeader });

        parsedHeaders.push(headerOption || unsupportedHeaderOption);
      } else {
        parsedHeaders.push(missingHeaderOption);
      }
    }

    this.headerMapping = parsedHeaders;
  }

  parseDataRows = () => {
    this.parseRows(this.dataRows);
  }

  parseRows = (dataRows) => {
    const rowsErrors = _.map(dataRows, value => this.parseRow(value));
    this.rowsErrors = rowsErrors;
  };

  parseRow = row =>
    _.map(row, (value, index) => {
      const normalizedValue = normalizeData(value);
      const headerOption = this.headerMapping[index];
      const fieldError = headerOption.validator(normalizedValue, this.config);

      return fieldError ? [index, fieldError] : null;
    }).filter(x => x);

  generateValidationResult = () => {
    // make up for the empty fields
    const headerWithErrorColumn = [...this.headerRow, ...Array(this.maxColumns - this.headerRow.length).fill(''), ErrorColumnTitle];
    const dataRowsWithError = _.map(
      this.dataRows,
      (row, index) => {
        const rowErrors = this.rowsErrors[index];

        // Fill empty column if needed
        const resultRow = row.length < this.maxColumns ? row.concat(Array(this.maxColumns - row.length).fill('')) : row;

        return [...resultRow,
          (rowErrors.length === 0 ? NoErrorLineMessage : this.mergeRowErrors(rowErrors))];
      }
    );

    const errorRowsNum = _.filter(this.rowsErrors, x => x.length > 0).length;
    this.validationResultFile = this.generateCsv(headerWithErrorColumn, dataRowsWithError);
    this.validationResultMeta = {
      goodRowsNum: (this.dataRows.length - errorRowsNum),
      errorRowsNum,
    };
  }

  mergeRowErrors = errors => _.map(_.unique(_.map(errors, x => x[1])), y => y.message).join(' ');

  generateUploadFileAsync = (perfMarker) => {
    const validRows = _.map(
      this.dataRows,
      (rowData, rowIndex) => {
        const rowErrors = this.rowsErrors[rowIndex];
        const errorsColumnIndexes = _.unique(_.map(rowErrors, x => x[0]));

        // If any field is error, empty this field
        // If any line is empty, remove this line
        let hasValidField = false;

        const resultRow = _.map(
          rowData,
          (field, colIndex) => {
            if (_.contains(errorsColumnIndexes, colIndex)) {
              return '';
            }
            if (normalizeData(field) !== '') {
              hasValidField = true;
            }
            return field;
          }
        ).concat(Array(this.maxColumns - rowData.length).fill(''));

        return hasValidField ? resultRow : null;
      }
    ).filter(x => x);
    if (perfMarker) {
      this.perfMarkerCombineHash = perfMarker.createChild('Audience_Combine_Hash');
      this.perfMarkerCombineHash.willInit();
    }

    // devide the large data into splited chunks
    // so that UI thread could update during the file hashing process
    const pacthedrValidRows = chunkArray(validRows, 10000);
    let withUiBlockerForHash = _.noop;
    if (validRows.length >= 200000) {
      withUiBlockerForHash = withUiBlock;
    }
    const processedPacthPromiseArr = [];

    _.each(pacthedrValidRows, (item) => {
      processedPacthPromiseArr.push(this.processedRows(item));
    });

    return Promise.all(processedPacthPromiseArr).then((result) => {
      let processedRows = [];
      _.each(result, (item) => {
        processedRows = processedRows.concat(item);
      });
      if (this.perfMarkerCombineHash) {
        this.perfMarkerCombineHash.done();
      }

      this.uploadFile =
        this.wrapStringAsFile(this.generateCsv(this.headerRow, processedRows.filter(x => x)));
    }).finally(withUiBlockerForHash());
  }

  processedRows(dataRows) {
    return new Promise((resolve) => {
      // settimeout makes UI thread could update page during every chunk interval
      setTimeout(() => {
        const processData = _.map(
          dataRows,
          row => _.map(
            this.headerMapping,
            (header, headerIndex) => {
              const fieldValue = row[headerIndex];

              if (!fieldValue) {
                return null;
              }

              const normalizedFieldValue = normalizeData(fieldValue);
              if (normalizedFieldValue === '') {
                return '';
              }

              return (header.isPII && !this.config.isFileHashed) ?
                SHA256.hashString(normalizedFieldValue) : fieldValue;
            }
          )
        );

        resolve(processData);
      }, 0);
    });
  }

  wrapStringAsFile(content) {
    return new Blob([content], { type: 'text/plain' });
  }

  generateCsv = (header, rows) => {
    const csvData = [header, ...rows];
    return Papa.unparse(csvData);
  }

  getValidationResultFile = () => this.validationResultFile;

  getValidationResultMeta = () => this.validationResultMeta;

  getUploadFile = () => this.uploadFile;
}
